/**
 * Weekly Progress Widget
 * 7-day bar chart showing practice minutes vs target.
 * Moved from StudentDashboardBeta (Phase 3.2 extraction).
 */

import React from 'react'
import type { BffWeeklyGoal } from '../../types/bff-v2'

export interface WeeklyProgressWidgetProps {
  goal: BffWeeklyGoal
  className?: string
}

const dayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

const WeeklyProgressWidget: React.FC<WeeklyProgressWidgetProps> = ({ goal, className = '' }) => {
  const perDayTarget = goal.target_min / 7
  const bars = goal.days.map((practiced, i) => {
    if (!practiced) return { height: 5, label: null }
    const practicedCount = goal.days.filter(Boolean).length || 1
    const share = goal.current_min / practicedCount
    const pct = Math.max(5, Math.min(100, (share / perDayTarget) * 100))
    return { height: pct, label: Math.round(share) }
  })
  const maxHeight = Math.max(...bars.map(b => b.height), 5)

  return (
    <div className={`flex-1 ${className}`}>
      <div className="flex items-end gap-[6px] h-[56px] mb-[5px]">
        {bars.map((bar, i) => {
          const isActive = goal.days[i]
          const displayHeight = `${Math.max(3, (bar.height / maxHeight) * 100)}%`
          return (
            <div key={i} className="flex-1 relative min-h-[3px]">
              <div
                className={`w-full rounded-[4px] transition-all duration-500 ${
                  isActive
                    ? 'opacity-100 bg-gradient-to-b from-[var(--color-neon)] to-[var(--color-cyan)]'
                    : 'opacity-15 bg-[var(--color-cyan)]'
                }`}
                style={{ height: displayHeight }}
              />
              {isActive && bar.label !== null && (
                <span className="absolute -top-[18px] left-1/2 -translate-x-1/2 text-[9px] font-bold
                                text-[var(--color-text-secondary)] whitespace-nowrap">
                  {bar.label}m
                </span>
              )}
            </div>
          )
        })}
      </div>
      <div className="flex gap-[6px]">
        {dayLabels.map((d, i) => (
          <span key={i} className="flex-1 text-center text-[10px] text-[var(--color-text-tertiary)] font-semibold">
            {d}
          </span>
        ))}
      </div>
    </div>
  )
}

export default WeeklyProgressWidget
